// =============================================================================
// campaign-client.ts — Client API du BC campaign ADMIN (S10), DOUBLABLE.
// =============================================================================
// Miroir des endpoints ADMIN de D2 (apps/api/src/campaign/, AdminSessionGuard).
// Comme les autres clients UI (suppliers, clients, legal, parameters), expose une
// INTERFACE (CampaignClient) consommée par les hooks TanStack ; l'impl HTTP réelle
// (fetch) viendra derrière la MÊME interface sans toucher l'UI (stub-first).
//
// DTO = miroirs HTTP (PAS le Published Language, PAS @jeko/db) : les enums sont
// répliqués en union de littéraux ASCII (parité Prisma CampaignState / CycleState /
// SeniorityLevel / ParticipationState — cf. packages/db/.../enums.ts). La SPA ne
// couple PAS au paquet DB ; les accents sont de l'i18n front (campaign-labels.ts).
//
// FIGEMENT catalog_version_id À LA CRÉATION (GATE_0 S10) : la création = intitulé
// SEUL ; l'estampillage de catalogVersionId (= ProfileCatalogVersion PUBLISHED
// courante) ET le REFUS si aucune PUBLISHED sont l'autorité D2. Côté front, ce refus
// se matérialise par une CampaignApiError « 409 figement » (kind === 'no-published-
// catalog') affichée telle quelle — l'UI ne réinterprète JAMAIS la règle métier.
//
// S10 couvre l'état BROUILLON (scope, planning, clonage). S11 (D4) ajoute, derrière la
// MÊME interface, les Prix Cibles & marges (UC-A-044) et l'OUVERTURE « Lancement »
// (UC-A-046, Planifiee → OuverteC1). Le câblage HTTP réel (campaign-client.http.ts)
// remplace le stub-first S10 derrière le DI seam, contre le CONTRAT DE ROUTES FIGÉ :
//
//   POST   /admin/campaigns                         {intitule}                   → 201 | 409
//   GET    /admin/campaigns                                                      → 200
//   GET    /admin/campaigns/:id                                                  → 200 | 404
//   POST   /admin/campaigns/:id/client-scopes       {clientSiren}                → 201 | 404
//   DELETE /admin/campaigns/:id/client-scopes/:scopeId                           → 204
//   POST   /admin/campaigns/:id/clients-diffusion   {enabled:boolean}      [B4]   → 204 | 404/400
//   POST   /admin/campaigns/:id/profile-scopes      {profileCode,seniorityLevel} → 201 | 404/422
//   DELETE /admin/campaigns/:id/profile-scopes/:scopeId                          → 204
//   POST   /admin/campaigns/:id/participations      {supplierSiret}              → 201 | 404 | 409
//   DELETE /admin/campaigns/:id/participations/:participationId                  → 204 | 409
//   POST   /admin/campaigns/:id/cycles              {cycleNumber,openAt,closeAt} → 201 | 404/422
//   POST   /admin/campaigns/:id/clone               {intitule}                   → 201 | 404/409
//   GET    /admin/campaigns/:id/target-prices                            [S11]   → 200 | 404
//   POST   /admin/campaigns/:id/target-prices       {SetTargetPriceConfig} [S11] → 201 | 404/422/409
//   GET    /admin/campaigns/:id/target-prices/defaults                  [L6]    → 200 | 404
//   POST   /admin/campaigns/:id/target-prices/apply-defaults {}         [L6]    → 200 | 404/409
//   POST   /admin/campaigns/:id/plan                 {}                [S11/E3b1] → 200 | 404/409
//   POST   /admin/campaigns/:id/open                                     [S11]   → 200 | 404/409
//
// Le corps 409 est STRUCTURÉ `{ statusCode, code, message }` (discriminateur D1) : l'adaptateur
// HTTP réel mappe `code` → CampaignApiErrorKind. Les routes [S11] sont FIGÉES côté D4 ; la
// parité runtime avec D2 (back, parallèle non committé) est un GATE de CONVERGENCE (D5).

import { HttpApiError } from '@/api/http-error';

// --- Enums miroirs HTTP (parité Prisma, ASCII — accents = i18n front) ---

/** État d'une campagne (9 états, parité Prisma CampaignState). */
export type CampaignStateDto =
  | 'Brouillon'
  | 'Planifiee'
  | 'OuverteC1'
  | 'AnalyseC1'
  | 'OuverteC2'
  | 'AnalyseC2'
  | 'Negociation'
  | 'Publiee'
  | 'Abandonnee';

/** État d'un cycle planifié (parité Prisma CycleState). */
export type CycleStateDto = 'Pendante' | 'Ouverte' | 'Cloturee' | 'Verrouillee';

/** Niveau de séniorité d'un profil (parité Prisma SeniorityLevel). */
export type SeniorityLevelDto = 'Debutant' | 'Maitrisant' | 'Expert' | 'Referent';

/** État d'une participation fournisseur (parité Prisma ParticipationState). */
export type ParticipationStateDto =
  | 'Invite'
  | 'EnSaisie'
  | 'Soumis'
  | 'Verrouille'
  | 'ReouvertNego';

/** Numéro de cycle d'une campagne : C1 ou C2 (énumération fermée, parité DTO D1). */
export type CycleNumberDto = 1 | 2;

/**
 * Motif d'abandon d'une campagne (UC-A-053, G0-S15-6). Union de 4 littéraux ASCII, parité
 * STRICTE de l'enum Prisma `CampaignAbandonMotif` (@jeko/db) — répliqué ici comme miroir HTTP
 * (la SPA ne couple PAS au paquet DB ; les libellés FR sont de l'i18n front, campaign-labels.ts).
 */
export type CampaignAbandonMotifDto =
  | 'AnnulationClient'
  | 'ErreurConfiguration'
  | 'Fusion'
  | 'Autre';

// --- Lectures (parité CampaignView / *View, DTO D1) ---

/** Ligne de la liste des campagnes (vue agrégée légère). */
export interface CampaignListItemDto {
  readonly campaignId: string;
  readonly intitule: string;
  readonly state: CampaignStateDto;
  readonly catalogVersionId: string;
  /**
   * P-8 — LIBELLÉ humain de la version de catalogue figée (`version_number` côté BC catalog). OPTIONNEL :
   * son absence est le contrat de DÉGRADATION (version introuvable ou port non câblé) et l'écran retombe
   * alors sur l'uuid tronqué. `catalogVersionId` reste l'identité ; ceci n'est qu'un affichage.
   */
  readonly catalogVersionNumber?: string;
  /** Horodatage de création (ISO 8601). */
  readonly createdAt: string;
}

/** Entrée de périmètre client (miroir ClientScopeView). */
export interface ClientScopeDto {
  readonly scopeId: string;
  readonly campaignId: string;
  readonly clientSiren: string;
  readonly addedAt: string;
}

/** Entrée de périmètre profil (miroir ProfileScopeView). */
export interface ProfileScopeDto {
  readonly scopeId: string;
  readonly campaignId: string;
  readonly profileCode: string;
  readonly seniorityLevel: SeniorityLevelDto;
}

/** Participation fournisseur (miroir SupplierParticipationView). */
export interface SupplierParticipationDto {
  readonly participationId: string;
  readonly campaignId: string;
  readonly supplierSiret: string;
  readonly state: ParticipationStateDto;
  readonly invitedAt: string | null;
  readonly submittedAt: string | null;
  readonly lastReminderAt: string | null;
}

/** Cycle planifié (miroir CycleScheduleView). Horodatages ISO 8601. */
export interface CycleScheduleDto {
  readonly cycleId: string;
  readonly campaignId: string;
  readonly cycleNumber: CycleNumberDto;
  readonly openAt: string;
  readonly closeAt: string;
  readonly state: CycleStateDto;
}

/**
 * Détail d'une campagne (vue agrégée : campagne + scopes + participations + cycles).
 * Miroir GET /admin/campaigns/:id (campagne + cycles + scopes — contrat D2).
 */
export interface CampaignDetailDto {
  readonly campaignId: string;
  readonly intitule: string;
  readonly state: CampaignStateDto;
  /** Version de catalogue FIGÉE à la création (immutable). */
  readonly catalogVersionId: string;
  /**
   * P-8 — LIBELLÉ humain de la version de catalogue figée (`version_number` côté BC catalog). OPTIONNEL :
   * son absence est le contrat de DÉGRADATION (version introuvable ou port non câblé) et l'écran retombe
   * alors sur l'uuid tronqué. `catalogVersionId` reste l'identité ; ceci n'est qu'un affichage.
   */
  readonly catalogVersionNumber?: string;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly lastModifiedAt: string;
  // V-CREATE / F0-d — dates GLOBALES (DBU §2.5 l.1125) + bandeau de provenance de duplication (l.1153).
  // ISO 8601, `null` si non posé. `clonedFrom*` non-null ⇒ campagne créée par duplication.
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly clonedFromIntitule: string | null;
  readonly clonedFromDate: string | null;
  // B4 (relevé GATE_HUMAN 2026-09-15) — état COURANT du flag « autoriser l'affichage des noms de clients ».
  // FAIL-CLOSED : `false` tant qu'un admin ne l'a pas explicitement autorisé. L'UI reflète cet état dans
  // la case à cocher ; l'autorité de la diffusion réelle reste l'API (carrier ACL campaign → client-reference).
  readonly clientsDiffusionEnabled: boolean;
  readonly clientScopes: readonly ClientScopeDto[];
  readonly profileScopes: readonly ProfileScopeDto[];
  readonly participations: readonly SupplierParticipationDto[];
  readonly cycles: readonly CycleScheduleDto[];
}

// --- V-CREATE : arbre du périmètre métier (familles → sous-familles → profils, DBU §2.5 l.1131) ---

/** Un profil de l'arbre du périmètre. `availableLevels` = niveaux inclus AUTO à la sélection. */
export interface CatalogTreeProfileDto {
  readonly profileCode: string;
  readonly libelle: string;
  readonly availableLevels: readonly SeniorityLevelDto[];
}

/** Une sous-famille de l'arbre du périmètre, portant ses profils. */
export interface CatalogTreeSubFamilyDto {
  readonly sousFamille: string;
  readonly profils: readonly CatalogTreeProfileDto[];
}

/** Un nœud RACINE de l'arbre du périmètre : une famille et ses sous-familles (miroir CatalogTreeNode). */
export interface CatalogTreeNodeDto {
  readonly famille: string;
  readonly sousFamilles: readonly CatalogTreeSubFamilyDto[];
}

// --- Corps des commandes (l'id de campagne vient TOUJOURS de l'URL, jamais du body) ---

/** Corps de création (UC-A-040) : l'intitulé EST le seul champ d'entrée (figement D2). */
export interface CreateCampaignInput {
  readonly intitule: string;
}

/** Corps d'ajout d'un client au périmètre (SIREN 9 chiffres). */
export interface AddClientScopeInput {
  readonly clientSiren: string;
}

/** Corps d'ajout d'un profil + niveau de séniorité au périmètre. */
export interface AddProfileScopeInput {
  readonly profileCode: string;
  readonly seniorityLevel: SeniorityLevelDto;
}

/**
 * Corps d'ajout EN LOT de profils (V-CREATE, inclusion AUTO des niveaux, DBU §2.5 l.1131). Le front
 * déplie chaque profil sélectionné en N items × availableLevels avant l'envoi ; l'API pose chacun
 * idempotemment par (profileCode, séniorité).
 */
export interface AddProfileScopeBatchInput {
  readonly items: readonly AddProfileScopeInput[];
}

/**
 * Corps de pose des dates GLOBALES de campagne (V-CREATE, F0-d, DBU §2.5 l.1125). ISO 8601 ou `null`
 * (efface la date). Cohérence (start < end) = autorité API (422 sinon).
 */
export interface SetCampaignDatesInput {
  readonly startDate: string | null;
  readonly endDate: string | null;
}

/** Corps d'ajout d'une participation fournisseur (SIRET 14 chiffres). */
export interface AddParticipationInput {
  readonly supplierSiret: string;
}

/** Corps de planning d'un cycle (C1 ou C2 ; openAt/closeAt en ISO 8601). */
export interface AddCycleInput {
  readonly cycleNumber: CycleNumberDto;
  readonly openAt: string;
  readonly closeAt: string;
}

/**
 * Corps de clonage (V-CREATE). `intitule` OPTIONNEL : absent ⇒ l'API pré-remplit « {intitulé source} —
 * {date courante} » (l.1149). La copie repart en Brouillon, provenance persistée (l.1153).
 */
export interface CloneCampaignInput {
  readonly intitule?: string | undefined;
}

// --- Prix Cibles (S11, UC-A-044 ; DTO figés D1) : consommés par la carte « Prix cibles » du hub Détail (ex-A-14 supprimé, L4) ---

/**
 * Configuration d'un Prix Cible pour un couple (profil, séniorité) — miroir HTTP de
 * SetTargetPriceConfigInputDto (D1). `targetPriceEur` est un ENTIER en euros (> 0) :
 * la validation de plage (entier strictement positif) est une garde UI (saisie) ET
 * l'autorité de l'API. S14-bis : marges de tolérance supprimées ; le seuil « Hors Jeux » est un
 * PARAMÈTRE GLOBAL unique (DBU §2.5 v2.2) — il n'est PLUS fixé par couple ici. v2.7 : le Prix Cible est
 * un REPÈRE ; le flag Hors Jeux compare le TJM à la médiane du couple × (1 + seuil), jamais au Prix Cible.
 */
export interface SetTargetPriceConfigInput {
  readonly profileCode: string;
  readonly seniorityLevel: SeniorityLevelDto;
  readonly targetPriceEur: number;
  /**
   * L10 (UC-A-044) : origine revendiquée. `PulseDefault` = acceptation de la valeur proposée (le serveur la
   * RECALCULE et refuse en 409 `TARGET_PRICE_DEFAULT_MISMATCH` si elle a changé) ; `AdminEntry` = saisie
   * manuelle. Absent ⇒ `AdminEntry` côté API.
   */
  readonly source?: TargetPriceSourceDto;
}

/** Origine d'un Prix Cible posé (L6) : valeur Pulse acceptée ou saisie admin. */
export type TargetPriceSourceDto = 'PulseDefault' | 'AdminEntry';

/** Lecture d'une configuration de Prix Cible (miroir TargetPriceConfigView, D1 + L6 additif). */
export interface TargetPriceConfigDto {
  readonly configId: string;
  readonly campaignId: string;
  readonly profileCode: string;
  readonly seniorityLevel: SeniorityLevelDto;
  readonly targetPriceEur: number;
  /** L6 : origine de la dernière pose. */
  readonly priceSource: TargetPriceSourceDto;
  /** L6 : P50 Pulse proposée au moment de la dernière pose (repère d'écart), `null` si aucune. */
  readonly defaultEurProposed: number | null;
  /** Sujet OIDC de l'admin validateur (null tant que non validée — la validation est S12). */
  readonly validatedBy: string | null;
  readonly validatedAt: string | null;
}

/**
 * L6 — un couple du PÉRIMÈTRE avec son prix posé (ou vide) et la valeur PROPOSÉE courante (P50 Pulse telle
 * quelle, calculée à la lecture, jamais persistée). Miroir TargetPriceCoupleView.
 */
export interface TargetPriceCoupleDto {
  readonly profileCode: string;
  readonly seniorityLevel: SeniorityLevelDto;
  readonly targetPriceEur: number | null;
  readonly priceSource: TargetPriceSourceDto | null;
  readonly defaultEurProposed: number | null;
  readonly validatedAt: string | null;
  /** Valeur proposée COURANTE ; `null` si aucune P50 éligible ou proposition désactivée. */
  readonly suggestedDefaultEur: number | null;
  /** Axe retenu : `__GLOBAL__` (tous secteurs) ou code secteur ; `null` sans suggestion. */
  readonly suggestedSector: string | null;
  readonly suggestedYear: number | null;
}

/** Axe « tous secteurs » de la résolution du défaut P50 Pulse (règle B1). */
export const TARGET_PRICE_GLOBAL_SECTOR = '__GLOBAL__';

/** L6 — GET /admin/campaigns/:id/target-prices/defaults (miroir TargetPriceDefaultsView). */
export interface TargetPriceDefaultsDto {
  readonly campaignId: string;
  /** Paramètre `target_price_pulse_default_enabled` (faux ⇒ aucune valeur proposée). */
  readonly pulseDefaultEnabled: boolean;
  /** Campagne terminale (Publiee, Abandonnee ou analyse produite) : pose refusée. */
  readonly locked: boolean;
  readonly couples: readonly TargetPriceCoupleDto[];
}

/** L6 — résultat de « Appliquer les valeurs Pulse » (miroir ApplyTargetPriceDefaultsResultView). */
export interface ApplyTargetPriceDefaultsResultDto {
  readonly campaignId: string;
  readonly applied: readonly TargetPriceConfigDto[];
  readonly withoutDefault: readonly {
    readonly profileCode: string;
    readonly seniorityLevel: SeniorityLevelDto;
  }[];
}

// --- Ouverture d'une campagne (S11, UC-A-046 « Lancement » ; Planifiee → OuverteC1) ---

/**
 * Résultat d'ouverture d'une campagne (miroir OpenCampaignResultView, D1). `cycle` =
 * numéro du cycle ouvert (1 pour C1) ; `openedState` = nouvel état (OuverteC1). L'ouverture
 * est l'acte qui émet DE.CampaignOpened côté API ; le FRONT relaie l'intention (id d'URL).
 */
export interface OpenCampaignResultDto {
  readonly campaignId: string;
  readonly cycle: CycleNumberDto;
  readonly openedState: CampaignStateDto;
}

// --- Pilotage des clôtures / ouverture C2 / abandon (S15, D1 contrats gelés) -------

/**
 * Résultat de la CLÔTURE de cycle (EARLY-CLOSE admin, UC-A-049 reframé, G0-S15-2). `closedState`
 * est DÉRIVÉ côté API du `cycle` (C1 → AnalyseC1, C2 → AnalyseC2 — jamais hardcodé). `closureType`
 * vaut toujours `'early'` pour la commande admin (l'auto-clôture `close_at` est portée par S14, hors
 * UI). Miroir HTTP de CloseCycleResultView (D1 §3.1).
 */
export interface CloseCycleResultDto {
  readonly campaignId: string;
  readonly cycle: CycleNumberDto;
  readonly closedState: CampaignStateDto;
  readonly closureType: 'auto' | 'early';
}

/** Corps de la clôture de cycle : le numéro du cycle à clôturer (C1 ou C2). */
export interface CloseCycleInput {
  readonly cycleNumber: CycleNumberDto;
}

/**
 * Résultat de l'OUVERTURE du Cycle 2 (UC-A-050, G0-S15-4). `cycle` vaut toujours 2 ; `openedState`
 * vaut `OuverteC2`. L'ouverture C2 est une commande admin CONDITIONNÉE à l'état AnalyseC1 (clôture +
 * analyse C1) ; jamais automatique. Miroir HTTP de OpenCampaignC2ResultView (D1 §3.2).
 */
export interface OpenCampaignC2ResultDto {
  readonly campaignId: string;
  readonly cycle: CycleNumberDto;
  readonly openedState: CampaignStateDto;
}

/**
 * Corps d'abandon (UC-A-053, G0-S15-6). `motif` est l'un des 4 motifs fermés ; `freeText` est un
 * commentaire libre OPTIONNEL (absent ⇒ non transmis). L'abandon est IRRÉVERSIBLE (→ Abandonnee).
 * `emailBody` (LOT-ABANDON item 30, DEC-20260722-11) = contenu de l'email d'abandon PRÉ-RÉDIGÉ
 * MODIFIABLE par l'admin avant envoi (OPTIONNEL ; absent ⇒ aucune notification). Miroir HTTP de
 * AbandonCampaignInputDto (D1 §3.3).
 */
export interface AbandonCampaignInput {
  readonly reason: {
    readonly motif: CampaignAbandonMotifDto;
    readonly freeText?: string | undefined;
  };
  readonly emailBody?: string | undefined;
}

/**
 * Résultat de l'abandon (UC-A-053). `abandonedState` vaut `Abandonnee` (terminal). `freeText` n'est
 * JAMAIS renvoyé (PII rédigée côté API) ; seul le `motif` (enum) revient. Miroir AbandonCampaignResultView.
 */
export interface AbandonCampaignResultDto {
  readonly campaignId: string;
  readonly abandonedState: CampaignStateDto;
  readonly abandonedAt: string;
  readonly motif: CampaignAbandonMotifDto;
}

// --- Clôture DÉFINITIVE (commande admin, UC-A-052, ARBITRAGE 1 S17) ----------

/**
 * Résultat de la CLÔTURE DÉFINITIVE d'une campagne (UC-A-052). Émet l'UNIQUE trigger du calcul
 * consolidé (DE.CampaignClosed) DEPUIS l'état d'analyse final, SANS faire transiter la campagne
 * (frontière gelée D0c) : `state` reste l'état d'analyse courant (AnalyseC1/AnalyseC2). `finalCycle`
 * est DÉRIVÉ de l'état côté API (AnalyseC1 → 1 monophase, AnalyseC2 → 2 biphase), JAMAIS saisi.
 * `alreadyClosed` vaut `true` si la commande était un NO-OP idempotent (campagne déjà clôturée /
 * consolidée), `false` si l'émission a eu lieu. Miroir HTTP de CloseCampaignResultView (D1).
 */
export interface CloseCampaignResultDto {
  readonly campaignId: string;
  readonly finalCycle: CycleNumberDto;
  readonly state: CampaignStateDto;
  readonly alreadyClosed: boolean;
}

/**
 * Erreur applicative renvoyée par l'API admin campaign (HTTP 4xx). L'UI l'affiche
 * telle quelle ; elle ne réinterprète JAMAIS la règle métier (autorité = API).
 *
 * `kind` discrimine les erreurs métier que l'UI doit présenter spécifiquement. Le back D1
 * a posé le DISCRIMINATEUR 409 STRUCTURÉ : le corps 409 est `{ statusCode, code, message }`
 * avec un `code` MACHINE stable. L'adaptateur HTTP réel MAPPE `code` → `kind` (autorité = API,
 * l'UI ne réinterprète JAMAIS la règle) :
 *   - 'no-published-catalog'        ← code CAMPAIGN_NO_PUBLISHED_CATALOG (création refusée :
 *     aucune version de catalogue PUBLISHED — figement impossible, GATE_0 S10).
 *   - 'clone-no-published-catalog'  ← code CAMPAIGN_CLONE_NO_PUBLISHED_CATALOG (clonage refusé :
 *     aucune PUBLISHED au moment du clone — re-figement impossible, OPP-01).
 *   - 'participation-phase-forbidden' ← code CAMPAIGN_PARTICIPATION_PHASE_FORBIDDEN (GARDE-INV5 :
 *     mutation de participation hors phase Brouillon — campagne déjà ouverte ≥ OuverteC1).
 *   - 'not-ready-to-plan'           ← code CAMPAIGN_NOT_READY_TO_PLAN (E-3b-1 : action « Planifier »
 *     refusée — paramétrage incomplet : scope vide, cycles absents/incohérents, aucun Prix Cible posé).
 *   - 'plan-wrong-state'            ← code CAMPAIGN_PLAN_WRONG_STATE (E-3b-1 : action « Planifier »
 *     refusée — la campagne n'est plus en Brouillon, ex. déjà planifiée / planification concurrente).
 *   - 'generic' : toute autre erreur applicative (message affiché tel quel).
 */
export type CampaignApiErrorKind =
  | 'no-published-catalog'
  | 'clone-no-published-catalog'
  // V-CREATE (DBU §2.5 l.1145) : duplication refusée — source non Clôturée/Archivée (état non terminal).
  | 'clone-source-not-terminal'
  | 'participation-phase-forbidden'
  | 'not-ready-to-plan'
  | 'plan-wrong-state'
  // --- Pilotage S15 (clôtures / ouverture C2 / abandon, D1 §3) ---
  | 'close-wrong-state'
  | 'close-unknown-cycle'
  | 'open-c2-wrong-state'
  | 'open-c2-empty-supplier-cohort'
  | 'open-c2-empty-profile-grid'
  | 'open-c2-no-validated-target-price'
  | 'open-c2-no-planned-cycle'
  | 'abandon-already-terminal'
  | 'abandon-invalid-state'
  // --- Clôture DÉFINITIVE (UC-A-052, ARBITRAGE 1 S17) ---
  | 'final-close-wrong-state'
  // --- Prix cibles (L6 / L6bis, UC-A-044) ---
  | 'target-price-locked'
  | 'target-price-default-mismatch'
  | 'target-price-busy'
  | 'generic';

/**
 * Codes machine STABLES du corps 409 structuré du BC campaign (autorité D1). Table de
 * correspondance code → kind UI consommée par l'adaptateur HTTP réel. FIGÉ : si un code
 * change côté API, le mapping ci-dessous (et lui seul) est mis à jour.
 */
export const CAMPAIGN_409_CODE_TO_KIND: Readonly<
  Record<string, CampaignApiErrorKind>
> = {
  CAMPAIGN_NO_PUBLISHED_CATALOG: 'no-published-catalog',
  CAMPAIGN_CLONE_NO_PUBLISHED_CATALOG: 'clone-no-published-catalog',
  CAMPAIGN_CLONE_SOURCE_NOT_TERMINAL: 'clone-source-not-terminal',
  CAMPAIGN_PARTICIPATION_PHASE_FORBIDDEN: 'participation-phase-forbidden',
  CAMPAIGN_NOT_READY_TO_PLAN: 'not-ready-to-plan',
  CAMPAIGN_PLAN_WRONG_STATE: 'plan-wrong-state',
  // Clôture de cycle (EARLY-CLOSE admin, D1 §3.1).
  CAMPAIGN_CLOSE_WRONG_STATE: 'close-wrong-state',
  CAMPAIGN_CLOSE_UNKNOWN_CYCLE: 'close-unknown-cycle',
  // Ouverture du Cycle 2 (D1 §3.2).
  CAMPAIGN_OPEN_C2_WRONG_STATE: 'open-c2-wrong-state',
  CAMPAIGN_OPEN_C2_EMPTY_SUPPLIER_COHORT: 'open-c2-empty-supplier-cohort',
  CAMPAIGN_OPEN_C2_EMPTY_PROFILE_GRID: 'open-c2-empty-profile-grid',
  CAMPAIGN_OPEN_C2_NO_VALIDATED_TARGET_PRICE: 'open-c2-no-validated-target-price',
  CAMPAIGN_OPEN_C2_NO_PLANNED_CYCLE: 'open-c2-no-planned-cycle',
  // Abandon irréversible (D1 §3.3).
  CAMPAIGN_ABANDON_ALREADY_TERMINAL: 'abandon-already-terminal',
  // LOT-ABANDON item 27 (DBU §2.5 l.1235) : abandon accessible QUE pour {Brouillon, Ouverte}.
  CAMPAIGN_ABANDON_INVALID_STATE: 'abandon-invalid-state',
  // Clôture DÉFINITIVE (UC-A-052, ARBITRAGE 1 S17) : refus si l'état n'est pas un état d'analyse final.
  CAMPAIGN_FINAL_CLOSE_WRONG_STATE: 'final-close-wrong-state',
  // Prix cibles (L6 / L6bis) : campagne terminale, valeur Pulse changée, clôture concurrente en cours.
  CAMPAIGN_TARGET_PRICE_LOCKED: 'target-price-locked',
  TARGET_PRICE_DEFAULT_MISMATCH: 'target-price-default-mismatch',
  CAMPAIGN_TARGET_PRICE_BUSY: 'target-price-busy',
};

export class CampaignApiError extends HttpApiError {
  public readonly kind: CampaignApiErrorKind;

  public constructor(
    message: string,
    kind: CampaignApiErrorKind = 'generic',
    status?: number,
  ) {
    super(message, status, 'CampaignApiError');
    this.kind = kind;
  }
}

/**
 * Contrat du client campaign ADMIN — miroir des endpoints D2 (AdminSessionGuard).
 * Aucune règle métier ici : transitions, estampillage catalog_version_id, refus
 * figement (409), validité temporelle des cycles sont l'autorité de l'API. L'UI
 * relaie l'intention. L'id de campagne est TOUJOURS la ressource d'URL (jamais
 * dans le corps).
 */
export interface CampaignClient {
  // --- Lectures ---

  /** GET /admin/campaigns — liste des campagnes. */
  listCampaigns(): Promise<readonly CampaignListItemDto[]>;
  /** GET /admin/campaigns/:id — détail (campagne + cycles + scopes + participations). */
  getCampaign(campaignId: string): Promise<CampaignDetailDto>;

  /**
   * GET /admin/campaigns/catalog-tree — arbre du périmètre métier (familles → sous-familles → profils)
   * de la version PUBLISHED courante (V-CREATE, DBU §2.5 l.1131). Vide si aucune version publiée.
   */
  listCatalogTree(): Promise<readonly CatalogTreeNodeDto[]>;

  // --- Création / clonage ---

  /**
   * POST /admin/campaigns — création (intitulé SEUL). 409 si aucune PUBLISHED
   * (CampaignApiError kind 'no-published-catalog'). Retourne la campagne créée.
   */
  createCampaign(input: CreateCampaignInput): Promise<CampaignListItemDto>;
  /** POST /admin/campaigns/:id/clone — clonage → nouvelle campagne Brouillon. */
  cloneCampaign(
    sourceCampaignId: string,
    input: CloneCampaignInput,
  ): Promise<CampaignListItemDto>;

  // --- Périmètre client ---

  /** POST /admin/campaigns/:id/client-scopes — ajoute un client (SIREN). */
  addClientScope(
    campaignId: string,
    input: AddClientScopeInput,
  ): Promise<ClientScopeDto>;
  /** DELETE /admin/campaigns/:id/client-scopes/:scopeId — retire un client. */
  removeClientScope(campaignId: string, scopeId: string): Promise<void>;

  /**
   * POST /admin/campaigns/:id/clients-diffusion — AUTORISE / révoque l'affichage des noms de clients
   * bénéficiaires (B4, GATE_HUMAN 2026-09-15). Corps `{ enabled: boolean }`. 204 au succès ; 404 si absente ;
   * 400 si `enabled` mal typé (autorité API). L'UI relit ensuite le détail pour refléter l'état.
   */
  setClientsDiffusion(campaignId: string, enabled: boolean): Promise<void>;

  // --- Périmètre profil ---

  /** POST /admin/campaigns/:id/profile-scopes — ajoute un profil + niveau. */
  addProfileScope(
    campaignId: string,
    input: AddProfileScopeInput,
  ): Promise<ProfileScopeDto>;
  /**
   * POST /admin/campaigns/:id/profile-scopes/batch — ajoute EN LOT des profils (× niveaux inclus AUTO,
   * V-CREATE l.1131). Idempotent par (profileCode, séniorité). Retourne le périmètre profil après l'ajout.
   */
  addProfileScopeBatch(
    campaignId: string,
    input: AddProfileScopeBatchInput,
  ): Promise<readonly ProfileScopeDto[]>;
  /** DELETE /admin/campaigns/:id/profile-scopes/:scopeId — retire un profil. */
  removeProfileScope(campaignId: string, scopeId: string): Promise<void>;

  // --- Participations fournisseurs ---

  /** POST /admin/campaigns/:id/participations — ajoute un fournisseur (SIRET). */
  addParticipation(
    campaignId: string,
    input: AddParticipationInput,
  ): Promise<SupplierParticipationDto>;
  /** DELETE /admin/campaigns/:id/participations/:participationId — retire un fournisseur. */
  removeParticipation(
    campaignId: string,
    participationId: string,
  ): Promise<void>;

  // --- Planning des cycles (1 ou 2 cycles) ---

  /** POST /admin/campaigns/:id/cycles — planifie OU met à jour (upsert) un cycle (C1 ou C2). */
  addCycle(campaignId: string, input: AddCycleInput): Promise<CycleScheduleDto>;
  /** DELETE /admin/campaigns/:id/cycles/:cycleNumber — supprime un cycle Pendante (204). */
  removeCycle(campaignId: string, cycleNumber: CycleNumberDto): Promise<void>;

  /**
   * POST /admin/campaigns/:id/dates — pose les dates GLOBALES de démarrage/fin (V-CREATE, F0-d, l.1125).
   * ISO 8601 ou null. 422 si incohérent (autorité API). Retourne la campagne (vue plate).
   */
  setCampaignDates(
    campaignId: string,
    input: SetCampaignDatesInput,
  ): Promise<CampaignListItemDto>;

  // --- Prix Cibles & marges (S11, UC-A-044) ---

  /** GET /admin/campaigns/:id/target-prices — configs de Prix Cible de la campagne. */
  listTargetPriceConfigs(
    campaignId: string,
  ): Promise<readonly TargetPriceConfigDto[]>;
  /**
   * POST /admin/campaigns/:id/target-prices — pose (upsert) la config de Prix Cible d'un
   * couple (profil, séniorité). 422 si valeur hors plage (autorité API).
   */
  setTargetPriceConfig(
    campaignId: string,
    input: SetTargetPriceConfigInput,
  ): Promise<TargetPriceConfigDto>;
  /**
   * GET /admin/campaigns/:id/target-prices/defaults (L6) — couples du périmètre, prix posés et valeurs
   * proposées (P50 Pulse), drapeaux `pulseDefaultEnabled` / `locked`.
   */
  listTargetPriceDefaults(campaignId: string): Promise<TargetPriceDefaultsDto>;
  /**
   * POST /admin/campaigns/:id/target-prices/apply-defaults (L6) — pose la valeur proposée sur les SEULS
   * couples sans prix (jamais d'écrasement). 409 LOCKED / BUSY.
   */
  applyTargetPriceDefaults(
    campaignId: string,
  ): Promise<ApplyTargetPriceDefaultsResultDto>;

  // --- Planification (« Planifier le paramétrage », S11, E-3b-1) ---

  /**
   * POST /admin/campaigns/:id/plan — valide le paramétrage (Brouillon → Planifiee). Corps VIDE
   * (l'id vient de l'URL). Transition INTERNE (aucun Domain Event). Retourne la campagne dans son
   * nouvel état (CampaignView plat). 409 si préconditions non satisfaites (autorité API) :
   *   - code CAMPAIGN_NOT_READY_TO_PLAN  → kind 'not-ready-to-plan'  (paramétrage incomplet :
   *     scope vide / cycles absents ou incohérents / aucun Prix Cible posé).
   *   - code CAMPAIGN_PLAN_WRONG_STATE   → kind 'plan-wrong-state'   (campagne plus en Brouillon).
   */
  planCampaign(campaignId: string): Promise<CampaignListItemDto>;

  // --- Ouverture (« Lancement », S11, UC-A-046) ---

  /**
   * POST /admin/campaigns/:id/open — ouvre la campagne (Planifiee → OuverteC1). Émet
   * DE.CampaignOpened côté API. 409 si périmètre incomplet / phase invalide (autorité API).
   */
  openCampaign(campaignId: string): Promise<OpenCampaignResultDto>;

  // --- Pilotage des clôtures / ouverture C2 / abandon (S15) -------------------

  /**
   * POST /admin/campaigns/:id/close-cycle — CLÔTURE anticipée (EARLY-CLOSE) d'un cycle (UC-A-049
   * reframé, G0-S15-2). Action admin OPTIONNELLE avant `close_at`. L'état cible (AnalyseC1/AnalyseC2)
   * est DÉRIVÉ du cycle par l'API. 409 : CAMPAIGN_CLOSE_WRONG_STATE / CAMPAIGN_CLOSE_UNKNOWN_CYCLE.
   */
  closeCycle(
    campaignId: string,
    input: CloseCycleInput,
  ): Promise<CloseCycleResultDto>;

  /**
   * POST /admin/campaigns/:id/open-c2 — OUVERTURE du Cycle 2 (UC-A-050, G0-S15-4). Corps VIDE.
   * CONDITIONNÉE à l'état AnalyseC1 (clôture + analyse C1) côté API ; jamais automatique. 409 :
   * CAMPAIGN_OPEN_C2_WRONG_STATE / _EMPTY_SUPPLIER_COHORT / _EMPTY_PROFILE_GRID /
   * _NO_VALIDATED_TARGET_PRICE / _NO_PLANNED_CYCLE.
   */
  openCampaignC2(campaignId: string): Promise<OpenCampaignC2ResultDto>;

  /**
   * POST /admin/campaigns/:id/abandon — ABANDON IRRÉVERSIBLE (UC-A-053, G0-S15-6) → Abandonnee
   * (terminal). Corps `{reason:{motif, freeText?}}`. 409 CAMPAIGN_ABANDON_ALREADY_TERMINAL si la
   * campagne est déjà dans un état terminal (irréversibilité). 422 si motif hors enum (autorité API).
   */
  abandonCampaign(
    campaignId: string,
    input: AbandonCampaignInput,
  ): Promise<AbandonCampaignResultDto>;

  /**
   * POST /admin/campaigns/:id/close — CLÔTURE DÉFINITIVE (UC-A-052, ARBITRAGE 1 S17). Corps VIDE
   * (l'id vient de l'URL, `finalCycle` DÉRIVÉ de l'état d'analyse final). Émet DE.CampaignClosed —
   * l'UNIQUE trigger du calcul consolidé — SANS faire transiter la campagne (frontière gelée). Action
   * TERMINALE et IRRÉVERSIBLE, IDEMPOTENTE (re-commande = NO-OP `alreadyClosed:true`). 404 si absente ;
   * 409 CAMPAIGN_FINAL_CLOSE_WRONG_STATE → kind 'final-close-wrong-state' si l'état n'est pas un état
   * d'analyse final (AnalyseC1 / AnalyseC2).
   */
  closeCampaign(campaignId: string): Promise<CloseCampaignResultDto>;
}
